import React, { useState, useEffect, useRef, useMemo } from "react";
import { useIntl, useLocation, history } from "umi";
import MetricCard from "@sg/pages/BusinessManagement/MetricCard";
import { PUBLISHING_PROJECT_REVENUE_CARD, PUBLISHING_PROJECT_MKT_COST_CARD, PUBLISHING_PROJECT_HEADCOUNT_CARD, PUBLISHING_PROJECT_TECH_CARD, COEFFICIENT } from '../config';
import linkSvg from '@/pages/strategic/assests/svg/link.svg';
import CardCommon from '@sg/components/CardCommon';
import QuarterlyReviewProgres from '@sg/pages/PublishingManagement/Project/components/QuarterlyReviewProgress';
import GameSelect from '@sg/components/GameSelect';
import { notification } from 'tea-component';
import {
  getPublishingProjectsList,
  getProjectReviews,
  updateProjectReview,
  getMktCostBudgetTotal,
  getProjectGrossRevenue,
  getProjectTechCost,
  getPublishingProjectHeadcount,
} from '@sg/services';
import PlainText from '@sg/components/PlainText';
import { rendTitle, filterFontSizeReview } from '@sg/utils/commonPageMethod';
import moment from 'moment';
import useReviewAutoScroll from './hooks/useReviewAutoScroll';
import { useReviewUpdatesAutoSave } from './hooks/useReviewUpdatesAutoSave';
import { getAppendixNotesPermission, getButtonPermission } from "@sg/_config/permission";
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { toTwoDecimalNumber } from '@sg/utils/common';
import { createBtnPoint, SGBuriedBtnPoint, PublishingProjectTab } from '@/pages/strategic/utils/buriedPoint';
import { EDIT_PAGE_KEY_MAP } from '@sg/hooks/useAutoSaveParams';
import AutoSaveModal from '@/pages/strategic/components/AutoSaveModal';
import RoutePrompt from '@/pages/strategic/components/RoutePrompt';
import AccessRestricted from '@sg/components/AccessRestricted';
import './index.scss'

const PROJECT_DASHBOARD_PATHS: Record<string, string> = {
  "PUBGM": "/dashboard/game/i_game/overview/daily",
  "HOK": "/dashboard/game/hok/overview/daily",
  "NIKKE - Overseas": "/dashboard/game/nikke_overall/overview/daily",
  "NIKKE - China": "/dashboard/game/nikke_cn/overview/daily",
  "Undawn": "/dashboard/game/cosall/overview/daily",
  "GSTm": "/dashboard/game/gst/overview/daily",
  "Don't Starve: Newhome - China": "/dashboard/game/starve_cn/overview/daily",
  "Exoborne (P2)": "/dashboard/game/rebirth/overview/daily-v2",
  "D5": "/dashboard/game/d5/overview/daily-v2",
};

export default function Studios(props: any) {
  const { selectedMonth, onProjectChange, onEditingChange, maxDataTime } = props;

  const selectedMonthMoment = useMemo(() => {
    return selectedMonth ? moment(selectedMonth, 'YYYY-MM') : null;
  }, [selectedMonth]);

  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();
  const location = useLocation();

  const [selectedProject, setSelectedProject] = useState({
    name: '',
    unified_id: '',
    game_id: '',
  });
  const [projectList, setProjectList] = useState<any[]>([]);
  const [mktCostData, setMktCostData] = useState<any>(null);
  const [revenueData, setRevenueData] = useState<any>(null);
  const [techCostData, setTechCostData] = useState<any>(null);
  const [headcountData, setHeadcountData] = useState<any>(null);

  const [reviewUpdateTime, setReviewUpdateTime] = useState<string>('');
  const [reviewsData, setReviewsData] = useState<any[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [hasProjectAccess, setHasProjectAccess] = useState<boolean>(true);

  // Refs
  const topRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [isNotesEditing, setIsNotesEditing] = useState(false);
  const [canEdit, setCanEdit] = useState(true); // Set based on user permissions

  // Auto-save hook for Review Updates
  const {
    autoSaveVisible: reviewAutoSaveVisible,
    hasReviewUnsavedChanges,
    handleReviewUpdatesEdit,
    handleAutoSaveCancel: handleReviewAutoSaveCancel,
    handleAutoSaveConfirm: handleReviewAutoSaveConfirm,
    clearReviewUpdatesAutoSave,
  } = useReviewUpdatesAutoSave(
    isEditing,
    reviewsData,
    selectedProject.unified_id,
    setReviewsData
  );

  // Auto-scroll to review section when returning from preview
  useReviewAutoScroll(reviewRef, reviewsLoading);

  const buttonPermission = useMemo(() => ({
    publishingReviewUpdates: getButtonPermission('publishing_project_publishingReviewUpdates'),
    Appendix: getButtonPermission('publishing_project_Appendix&Notes'),
  }), [permissionReady]);

  // Memoized project information for buried point extInfo
  const projectInfo = useMemo(() => {
    const projectName = selectedProject?.name || 'Unknown';
    const projectId = selectedProject?.unified_id || '';
    return `Project: ${projectName}${projectId ? ` (ID: ${projectId})` : ''}`;
  }, [selectedProject?.name, selectedProject?.unified_id]);

  const fetchProjectData = async () => {
    try {
      const res = await getPublishingProjectsList();
      // console.log('Publishing Projects List Response:', res);

      // Transform response to match GameSelect expected format
      return res?.data?.projects?.map((item: any) => ({
        name: item.name,
        cover: item.cover,
        unified_id: item.id,
        game_id: item.game_id,
        // originalData: item,
      })) || [];
    } catch (error) {
      console.error('Error fetching publishing projects list:', error);
      notification.error({
        description: 'Failed to load publishing projects list. Please try again.',
      });
      return [];
    }
  };

  const handleProjectChange = (
    studio: any,
    triggerSource: 'auto' | 'click' = 'click',
  ) => {
    // Track buried point only for real user selection
    if (triggerSource === 'click') {
      const projectName = studio?.name || 'Unknown';
      const projectId = studio?.unified_id || '';
      const btnPoint = createBtnPoint(
        PublishingProjectTab,
        'projectSelect',
        `Project Select - Project: ${projectName}${projectId ? ` (ID: ${projectId})` : ''}`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    }
    // URL update is now handled by GameSelect component via urlParamKey prop
  };

  const handleProjectDataLoaded = (data: any[]) => {
    setProjectList(data);
    setHasProjectAccess(data && data.length > 0); // if no data meaning no access
    // GameSelect handles initial selection based on defaultUnifiedId prop
  };

  // Sync selectedProject from URL params + projectList (similar to activeSegment pattern)
  // This ensures selectedProject only changes when URL actually changes, enabling RoutePrompt interception
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlProjectId = searchParams.get('project_id');

    // Only sync if we're on the "publishingProject" subtitle
    const currentSubtitle = searchParams.get('subtitle');
    if (currentSubtitle !== 'publishingProject') {
      return;
    }

    if (urlProjectId && projectList.length > 0) {
      // Find project from projectList that matches URL project_id
      const projectFromUrl = projectList.find((project: any) => project.unified_id === urlProjectId);
      if (projectFromUrl) {
        // Update selectedProject based on URL - React will handle duplicate state updates efficiently
        setSelectedProject({
          name: projectFromUrl.name,
          unified_id: projectFromUrl.unified_id,
          game_id: projectFromUrl.game_id || '',
        });
      }
    } else if (!urlProjectId && projectList.length > 0) {
      // If URL has no project_id, select first project as default
      const firstProject = projectList[0];
      if (firstProject) {
        setSelectedProject({
          name: firstProject.name,
          unified_id: firstProject.unified_id,
          game_id: firstProject.game_id || '',
        });
      }
    }
  }, [location.search, projectList]);

  // Notify parent when project changes
  useEffect(() => {
    if (onProjectChange && selectedProject.unified_id) {
      onProjectChange(selectedProject.unified_id);
    }
  }, [selectedProject.unified_id, onProjectChange]);

  const fetchPublishingReviewList = async () => {
    if (!selectedProject.unified_id) {
      setReviewsData([]);
      setCanEdit(false);
      setReviewsLoading(false);
      return;
    }

    try {
      setReviewsLoading(true);
      const res = await getProjectReviews({ projectId: selectedProject.unified_id });

      // Extract reviewItems array and updateTime from new API structure
      // 过滤历史字体大小
      const listTemp = res?.data?.reviewItems || [];
      const reviewItems = filterFontSizeReview(listTemp);
      const apiUpdateTime = res?.data?.updateTime || '';

      // Update the review update time state
      setReviewUpdateTime(apiUpdateTime);

      // Transform new API structure to match component expectations
      const transformedData = reviewItems.map((review: any) => {
        // console.log('Processing review:', { id: review.id, attach: review.attach, file_name: review.file_name });

        // Handle attach and file_name - they can be empty strings, arrays, or comma-separated strings
        let attachArray = [];
        if (Array.isArray(review.attach)) {
          attachArray = review.attach;
        } else if (review.attach && typeof review.attach === 'string') {
          // Check if it's a comma-separated string
          attachArray = review.attach.includes(',') ? review.attach.split(',').map((s: string) => s.trim()) : [review.attach];
        }

        let fileNameArray = [];
        if (Array.isArray(review.file_name)) {
          fileNameArray = review.file_name;
        } else if (review.file_name && typeof review.file_name === 'string') {
          // Check if it's a comma-separated string
          fileNameArray = review.file_name.includes(',') ? review.file_name.split(',').map((s: string) => s.trim()) : [review.file_name];
        }

        // console.log('Parsed arrays:', { attachArray, fileNameArray });

        return {
          id: review.id,
          phase: review.phase, // Phase from API (e.g., "MR", "GL", "CBT")
          date: moment(review.date).format('YYYY-MM-DD'), // Format date from "2022-12-07 00:00:00" to "2022-12-07"
          status: review.status?.toString() || '0', // Convert number status to string
          description: review.description || '',
          files: attachArray.map((attach: string, index: number) => {
            const fileName = fileNameArray[index] || `File ${index + 1}`;

            return {
              name: fileName,
              path: attach, // COS path for file operations
              // Don't pre-set format and size - let COSFileList handle the splitting
            };
          })
        };
      }) || [];

      // console.log('Transformed Review Data:', transformedData);
      setReviewsData(transformedData);
      setCanEdit(transformedData.length > 0);
    } catch (error) {
      console.error('Error fetching project reviews:', error);
      setReviewsData([]);
      setCanEdit(false);
      notification.error({
        description: 'Failed to fetch publishing review updates. Please try again.',
      });
    } finally {
      setReviewsLoading(false);
    }
    // ==========================================================================
  };

  const fetchMktCostBudget = async () => {
    if (!selectedProject.unified_id) {
      setMktCostData(null);
      return;
    }

    try {
      // Build payload with optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { projectId: string; datatime?: string } = { projectId: selectedProject.unified_id };
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const res: any = await getMktCostBudgetTotal(payload);
      setMktCostData(res?.data);
    } catch (error) {
      console.error('Error fetching MKT cost budget:', error);
      setMktCostData(null);
      notification.error({
        description: 'Failed to fetch MKT Cost Budget data. Please try again.',
      });
    }
  };

  const fetchGrossRevenue = async () => {
    if (!selectedProject.unified_id) {
      setRevenueData(null);
      return;
    }

    try {
      // Build payload with optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { projectId: string; datatime?: string } = { projectId: selectedProject.unified_id };
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const res: any = await getProjectGrossRevenue(payload);
      setRevenueData(res?.data);

    } catch (error) {
      console.error('Error fetching gross revenue:', error);
      setRevenueData(null);
      notification.error({
        description: 'Failed to fetch Gross Revenue data. Please try again.',
      });
    }
  };

  const fetchTechCost = async () => {
    if (!selectedProject.unified_id) {
      setTechCostData(null);
      return;
    }

    try {
      // Build payload with optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { projectId: string; datatime?: string } = { projectId: selectedProject.unified_id };
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const res: any = await getProjectTechCost(payload);
      setTechCostData(res?.data);

    } catch (error) {
      console.error('Error fetching tech cost:', error);
      setTechCostData(null);
      notification.error({
        description: 'Failed to fetch Tech Cost data. Please try again.',
      });
    }
  };

  const fetchProjectHeadcount = async () => {
    if (!selectedProject.unified_id) {
      setHeadcountData(null);
      return;
    }

    try {
      // Build payload with optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { project_id: string; datatime?: string } = { project_id: selectedProject.unified_id };
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const res: any = await getPublishingProjectHeadcount(payload);
      setHeadcountData(res?.data);

    } catch (error) {
      console.error('Error fetching project headcount:', error);
      setHeadcountData(null);
      notification.error({
        description: 'Failed to fetch Project Headcount data. Please try again.',
      });
    }
  };

  // Fetch data when selected project or selectedMonth changes
  useEffect(() => {
    fetchMktCostBudget();
    fetchGrossRevenue();
    fetchTechCost();
    fetchProjectHeadcount();
    fetchPublishingReviewList();
  }, [selectedProject.unified_id, selectedMonth]);

  // Exit editing mode when project changes
  useEffect(() => {
    // Exit editing mode for Publishing Review Updates
    if (isEditing) {
      setIsEditing(false);
      clearReviewUpdatesAutoSave();
    }
    // PlainText (Appendix & Notes) will automatically reset editing state
    // because we use a key prop that changes with selectedProject.unified_id,
    // forcing the component to remount and reset all internal state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject.unified_id]);

  // Notify parent component when any editing state changes
  useEffect(() => {
    if (onEditingChange) {
      const isAnyEditing = isEditing || isNotesEditing;
      onEditingChange(isAnyEditing);
    }
  }, [isEditing, isNotesEditing, onEditingChange]);

  // Edit mode handlers
  const handleEdit = () => {
    // Track buried point for Publishing Review Updates Edit button
    const btnPoint = createBtnPoint(
      PublishingProjectTab,
      'publishingReviewUpdatesEdit',
      `Publishing Review Updates - Edit - ${projectInfo}`
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    // Original edit logic wrapped in a function
    const originalEditHandler = () => {
      setIsEditing(true);
    };

    // Check for auto-save before entering edit mode
    handleReviewUpdatesEdit(originalEditHandler);
  };

  const handleSave = async () => {
    try {

      // Update each review that has been modified
      const updatePromises = reviewsData.map(async (review) => {
        const payload = {
          id: review.id,
          description: review.description,
          documents: review.files.map((file: any) => file.path || file.url),
          file_name: review.files.map((file: any) => file.name || file.path?.split('/').pop() || ''),
          // file_name: review.files.map((file: any) => {
          //   // Remove file extension from name
          //   const dotIndex = file.name?.lastIndexOf('.');
          //   return dotIndex > 0 ? file.name.substring(0, dotIndex) : file.name;
          // }),
        };

        // console.log('Updating review:', payload);
        return updateProjectReview(payload);
      });

      await Promise.all(updatePromises);

      notification.success({
        description: 'Operate Successfully',
      });

      // Clear auto-save after successful save
      clearReviewUpdatesAutoSave();

      setIsEditing(false);

      // Refetch data to get the latest from server
      fetchPublishingReviewList();
    } catch (error) {
      console.error('Error saving changes:', error);
      notification.error({
        description: 'Failed to save Publishing Review Updates. Please try again.',
      });
    }
  };

  const handleCancel = () => {
    // Clear auto-save when canceling
    clearReviewUpdatesAutoSave();
    setIsEditing(false);
    // Optionally refetch data to discard changes
    fetchPublishingReviewList();
  };

  // Dynamic MetricCard configs
  const mktCostCardConfig = useMemo(() => {
    return PUBLISHING_PROJECT_MKT_COST_CARD(mktCostData);
  }, [mktCostData]);

  const revenueCardConfig = useMemo(() => {
    return PUBLISHING_PROJECT_REVENUE_CARD(revenueData);
  }, [revenueData]);

  const techCostCardConfig = useMemo(() => {
    return PUBLISHING_PROJECT_TECH_CARD(techCostData);
  }, [techCostData]);

  const headcountCardConfig = useMemo(() => {
    return PUBLISHING_PROJECT_HEADCOUNT_CARD(headcountData);
  }, [headcountData]);

  useEffect(() => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 400)
  }, [])

  // Get project_id from URL to pass to GameSelect
  const urlProjectId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('project_id') || undefined;
  }, [location.search]);

  const dashboardPath = PROJECT_DASHBOARD_PATHS[selectedProject.name];

  // Show Access Restricted if no studios available after permission filtering
  if (!hasProjectAccess) {
    return (
      <AccessRestricted />
    );
  }

  return (
    <div>
      {/* <div ref={topRef} style={{position: 'relative', top: '-160px'}}></div> */}
      <GameSelect
        fetchData={fetchProjectData}
        onChange={handleProjectChange}
        onDataLoaded={handleProjectDataLoaded}
        defaultUnifiedId={urlProjectId}
        urlParamKey="project_id"
        urlBasePath="/strategic/home"
        urlUpdateCondition={(loc) => {
          const params = new URLSearchParams(loc.search);
          return params.get('subtitle') === 'publishingProject';
        }}
      />
      <div className='dbs-home-studio-project-card'>
        <div className="dbs-home-studio-project-card-header">
          <div className="dbs-home-studio-project-card-header-left">
            <span className="dbs-home-studio-project-card-title">{`${formatMessage({ id: 'dbs_key_metrics' })}_${selectedProject.name}`}</span>
          </div>
          <div
            className="dbs-home-studio-project-card-header-right"
            onClick={() => {
              if (dashboardPath) {
                // Track buried point for Dashboard Link Click
                const btnPoint = createBtnPoint(
                  PublishingProjectTab,
                  'dashboardLinkClick',
                  `Dashboard Link Click - ${projectInfo}`
                );
                if (btnPoint) {
                  SGBuriedBtnPoint(btnPoint);
                }

                // Use history.push for SPA navigation - no full page reload, API calls can complete naturally
                history.push(dashboardPath);
              }
            }}
            style={{ cursor: dashboardPath ? 'pointer' : 'default' }}
            role="button"
          >
            <span className="dbs-home-studio-project-card-more">
              {formatMessage({ id: 'dbs_more_available_at_dashboad' })}
            </span>
            <img src={linkSvg} className="dbs-home-studio-project-card-link" alt="dashboard link" />
          </div>
        </div>
        <div className="dbs-home-studio-project-card-body">
          <MetricCard
            key={revenueCardConfig.title}
            coefficient={COEFFICIENT}
            config={revenueCardConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
          />
          <MetricCard
            key={mktCostCardConfig.title}
            coefficient={COEFFICIENT}
            config={mktCostCardConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
          />
          <MetricCard
            key={headcountCardConfig.title}
            coefficient={COEFFICIENT}
            config={headcountCardConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
            valueFormatter={toTwoDecimalNumber}
          />
          <MetricCard
            key={techCostCardConfig.title}
            coefficient={COEFFICIENT}
            config={techCostCardConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
          />
        </div>
      </div>

      <div ref={reviewRef}></div>
      <CardCommon
        className="dbs-publishing-review-updates-card"
        title={rendTitle({
          title: `${formatMessage({ id: 'dbs_publishing_review_updates' })}_${selectedProject.name}`,
          tips: reviewUpdateTime ? `${formatMessage({ id: 'dbs_update_on' })} ${reviewUpdateTime}` : '',
          tipString: 'Data is updated by the Business Operations Management team.'
        })}

        hasEditMode={true}
        canEdit={buttonPermission.publishingReviewUpdates && canEdit}
        isEditing={isEditing}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
      >
        {/* 进度组件 */}
        <QuarterlyReviewProgres
          data={reviewsData}
          isEditing={isEditing}
          canEdit={buttonPermission.publishingReviewUpdates && canEdit}
          selectedProjectId={selectedProject.unified_id}
          onChange={(updatedData) => {
            setReviewsData(updatedData);
          }}
        />

      </CardCommon>

      <PlainText
        key={`plaintext-${selectedProject.unified_id}`}
        title={`${formatMessage({ id: 'dbs_appendix_notes' })}_${selectedProject.name}`}
        url="/api/v1/mgmt_pc/note/get"
        editUrl="/api/v1/mgmt_pc/note/update"
        params={{
          project_id: Number(selectedProject.unified_id),
          source: "project",
          logical_flag: "has_date_latest"
        }}
        selectedMonth={selectedMonthMoment}
        maxDataTime={maxDataTime}
        showTimeInTitle={true}
        editable={true}
        userPermissions={{ canEdit: buttonPermission.Appendix, noAccessHide: getAppendixNotesPermission('publishing_project_Appendix&Notes') }}
        tipString={'Data is updated by the Business Operations Management team.'}
        onEditingChange={setIsNotesEditing}
        onEditClick={(type) => {
          // Track buried point for Appendix Notes Edit button or Add Content link click
          const extInfo = type === 'Add Content Link'
            ? `Appendix Notes Edit - Add Content Link - ${projectInfo}`
            : `Appendix Notes Edit - Edit Button - ${projectInfo}`;
          const btnPoint = createBtnPoint(
            PublishingProjectTab,
            'appendixNotes',
            extInfo
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }}
        // Auto-save feature (with project_id to differentiate between projects)
        autoSavePageKey={EDIT_PAGE_KEY_MAP.publishing_project_notes}
        autoSaveId={selectedProject.unified_id}
      />

      {/* Auto-save Modal for Review Updates */}
      <AutoSaveModal
        visible={reviewAutoSaveVisible}
        onConfirm={() => handleReviewAutoSaveConfirm(() => setIsEditing(true))}
        onClose={() => handleReviewAutoSaveCancel(() => setIsEditing(true))}
      />

      {/* 🚀 RoutePrompt for Publishing Review Updates */}
      <RoutePrompt
        when={hasReviewUnsavedChanges}
        onOk={async () => {
          await handleSave();
        }}
      />
    </div>
  );
};
